import {useCallback, useRef, useState} from 'react';

import {
    EventTypes,
    HeadlessButtonRenderOptions,
    PaymentMethodInfo,
    PrimerHeadlessCheckout,
} from '@primer-io/checkout-web';

import {useDispatch} from 'react-redux';

import {PAYMENTS} from 'desktop/pages/checkout/constants/paymentStep';
import {useConfigureAndStartPrimer} from 'desktop/pages/checkout/hooks/primer/useConfigureAndStartPrimer';

import {setOptionPaymentMethod} from 'desktop/pages/checkout/store/reducers/analytics/analytics';

import {sendPayerInfoToSaga} from 'desktop/pages/checkout/store/reducers/paymentStep/payerInfo/payerInfo';

import {usePaymentCreationHandlers} from './usePaymentCreationHandlers';

export type PrimerNativeButtonConfigureType = {
    buttonId: string;
    buttonRenderOptions?: HeadlessButtonRenderOptions;
    externalMethodType: 'PAYPAL' | 'GOOGLE_PAY' | 'APPLE_PAY';
    internalMethodType: PAYMENTS;
    onClick?: () => void;
};

/**
 * Hook for primer native buttons
 */
export const usePrimerNativeButton = ({
    buttonId,
    buttonRenderOptions = {},
    externalMethodType,
    internalMethodType,
    onClick,
}: PrimerNativeButtonConfigureType) => {
    const dispatch = useDispatch();
    const primerRef = useRef<PrimerHeadlessCheckout | null>(null);
    const [isReady, setIsReady] = useState(false);
    const [isAvailable, setIsAvailable] = useState(false);
    const optionsRef = useRef(buttonRenderOptions);
    const onClickRef = useRef(onClick);
    const paymentCreationHandlers = usePaymentCreationHandlers(internalMethodType, primerRef);

    const onAvailablePaymentMethodsLoad = useCallback(
        async (paymentMethods: PaymentMethodInfo[]) => {
            if (
                !primerRef.current ||
                !paymentMethods.find(method => method.type === externalMethodType)
            )
                return;
            setIsAvailable(true);
            const paymentMethodManager =
                await primerRef.current.createPaymentMethodManager(externalMethodType);
            const button = paymentMethodManager?.createButton();
            button?.render(buttonId, optionsRef.current);
            if (onClickRef.current) button?.addEventListener(EventTypes.CLICK, onClickRef.current);
        },
        [buttonId, externalMethodType],
    );

    useConfigureAndStartPrimer({
        ...paymentCreationHandlers,
        onAvailablePaymentMethodsLoad,
        onPrimerHeadlessReady: primerHeadless => {
            primerRef.current = primerHeadless;
        },
        onReady: () => setIsReady(true),
        runSavePayment: ({orderId, token}) =>
            dispatch(
                sendPayerInfoToSaga({method: internalMethodType, orderIncrementId: orderId, token}),
            ),
        setOptionPayment: () => dispatch(setOptionPaymentMethod()),
        skipVaultManager: true,
    });

    return {
        isAvailable,
        isReady,
    };
};
